import { Component, EventEmitter, Output, inject, signal, OnInit, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ApiService } from '../../../../core/services/api.service';

@Component({
  selector: 'app-chat-history-sidebar',
  standalone: true,
  imports: [CommonModule],
  providers: [DatePipe],
  template: `
    <div class="flex flex-col h-full w-full">
      <div class="p-2 pb-0">
        <div (click)="onNewChat()"
          class="p-2.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors cursor-pointer flex items-center gap-3 group">
          <div class="text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200 transition-colors">
             <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.375 2.625a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4Z"></path></svg>
          </div>
          <span class="text-[13px] font-medium text-slate-700 dark:text-slate-300 transition-colors">New chat</span>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto p-2 space-y-1 scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700">
        @if (isChatSessionsLoading()) {
          <div class="flex items-center justify-center py-4">
            <div class="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        } @else if (chatSessions().length === 0) {
          <div class="text-center py-6 text-[12px] font-medium text-slate-400 dark:text-slate-500">
            No chat history found.
          </div>
        } @else {
          @for (session of chatSessions(); track session.id || session.sessionId) {
            <div (click)="loadChatSession(session)"
              class="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800/40 transition-colors cursor-pointer border border-transparent hover:border-slate-200 dark:hover:border-slate-700/50 group">
              <p class="text-[12px] font-medium text-slate-700 dark:text-slate-300 truncate" 
                 [title]="session.chatTitle || session.title || session.query || 'Chat Session'">
                 {{ session.chatTitle || session.title || session.query || 'Chat Session' }}
              </p>
              <p class="text-[10px] text-slate-400 mt-0.5">
                 {{ (session.createdAt || session.updatedAt || session.timestamp) | date:'MMM d, h:mm a' }}
              </p>
            </div>
          }
        }
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [':host { display: block; height: 100%; width: 100%; }']
})
export class ChatHistorySidebarComponent implements OnInit {
  @Input() chatType: 'MONTHLY_REPORT_1' | 'CALL_AUDIT_HISTORY' | 'TRAINING_ANALYTICS' | 'ARRANGMENT_CRON' | 'CRON_PAYMENTS' = 'MONTHLY_REPORT_1';
  private _apiService = inject(ApiService);

  // Static cache to persist data across component creations/destructions (when toggling sidebar)
  static cachedSessions: any[] | null = null;
  static cachedProductId: any = null;

  chatSessions = signal<any[]>([]);
  isChatSessionsLoading = signal<boolean>(false);

  @Output() sessionSelected = new EventEmitter<any>();
  @Output() newChatRequested = new EventEmitter<void>();

  ngOnInit() {
    const currentProductId = this._apiService.selectedProductId();

    // Use cache if available for the same product, don't show loading state
    if (ChatHistorySidebarComponent.cachedSessions && ChatHistorySidebarComponent.cachedProductId === currentProductId) {
      this.chatSessions.set(ChatHistorySidebarComponent.cachedSessions);
      // We can optionally fetch in background, but the user requested loading only once
      // this.fetchChatSessions(false); 
    } else {
      this.fetchChatSessions();
    }
  }

  fetchChatSessions() {
    this.isChatSessionsLoading.set(true);
    const productId = this._apiService.selectedProductId();
    const products = this._apiService.products();

    const params: any = {
      page: 1,
      limit: 20,
      type: this.chatType
    };

    if (productId) {
      const selectedProduct = products.find(p => p.id === productId);
      if (selectedProduct && selectedProduct.pid !== undefined) {
        params.productPid = selectedProduct.pid;
      } else {
        // Fallback: Try to resolve pid from sessionStorage dynamic_routes since products list might still be loading from network
        let resolvedPid: any = null;
        try {
          const stored = sessionStorage.getItem('dynamic_routes');
          if (stored) {
            const dynamicRoutes = JSON.parse(stored);
            const matched = dynamicRoutes.find((p: any) => p.product?.id === productId);
            if (matched && matched.product?.pid !== undefined) {
              resolvedPid = matched.product.pid;
            }
          }
        } catch (e) {
          console.error('Error resolving productPid from dynamic_routes:', e);
        }
        
        params.productPid = resolvedPid !== null ? resolvedPid : productId;
      }
    }

    this._apiService.get('/summary/sessions', params).subscribe({
      next: (res: any) => {
        let sessions: any[] = [];
        if (res && res.data && res.data.sessionList && Array.isArray(res.data.sessionList)) {
          sessions = res.data.sessionList;
        } else if (res && res.sessionList && Array.isArray(res.sessionList)) {
          sessions = res.sessionList;
        } else if (res && res.data && Array.isArray(res.data)) {
          sessions = res.data;
        } else if (res && Array.isArray(res)) {
          sessions = res;
        } else if (res && res.results) {
          sessions = res.results;
        }

        // Filter out sessions that do not have a chatTitle
        sessions = sessions.filter((session: any) => !!session.chatTitle);

        this.chatSessions.set(sessions);

        // Update static cache
        ChatHistorySidebarComponent.cachedSessions = sessions;
        ChatHistorySidebarComponent.cachedProductId = productId;

        this.isChatSessionsLoading.set(false);
      },
      error: (err) => {
        console.error('Error fetching chat sessions:', err);
        this.isChatSessionsLoading.set(false);
      }
    });
  }

  // Optional: Public method if another component needs to force refresh the cache
  forceRefresh() {
    this.fetchChatSessions();
  }

  loadChatSession(session: any) {
    this.sessionSelected.emit(session);
  }

  onNewChat() {
    this.newChatRequested.emit();
  }
}
